import type { ReactNode } from 'react';
import classNames from 'classnames';

import styles from './Badge.module.scss';

export type BadgeTone =
    | 'neutral'
    /** The page's own text on the light border, for a label that should not read as lavender. */
    | 'plain'
    | 'accent'
    | 'info'
    | 'success'
    | 'warning'
    | 'elevated'
    | 'danger'
    | 'internal';

export type BadgeSize = 'xs' | 'sm' | 'md' | 'lg';

/** `pill` is round; `tag` is a square uppercase label, such as a fish group's kind. */
export type BadgeShape = 'pill' | 'tag';

/** `soft` is a tint with a border, `solid` the full fill, `outline` the border alone. */
export type BadgeVariant = 'soft' | 'solid' | 'outline';

export interface BadgeStyle {
    tone?: BadgeTone;
    size?: BadgeSize;
    variant?: BadgeVariant;
    shape?: BadgeShape;
}

export interface BadgeProps extends BadgeStyle {
    children: ReactNode;
    /** Decorative; takes the badge's text colour through `currentColor`. */
    icon?: ReactNode;
    /** Drawn after the label, such as a trend arrow; sized like `icon`. */
    iconEnd?: ReactNode;
    /** A filled circle in the tone's colour before the label. */
    dot?: boolean;
    className?: string;
    title?: string;
}

/**
 * The badge look on an element that is not a Badge, such as a button that has to read as
 * one. Prefer the Badge component for anything static.
 */
export function badgeClassName(style: BadgeStyle = {}, className?: string): string {
    const { tone = 'neutral', size = 'sm', variant = 'soft', shape = 'pill' } = style;
    return classNames(
        styles.badge,
        tone !== 'neutral' && styles[tone],
        variant !== 'soft' && styles[variant],
        styles[size],
        shape === 'tag' && styles.tag,
        className
    );
}

export function Badge(props: BadgeProps) {
    const {
        children,
        tone,
        size,
        variant,
        shape,
        icon,
        iconEnd,
        dot = false,
        className,
        title,
    } = props;

    return (
        <span
            className={badgeClassName({ tone, size, variant, shape }, className)}
            data-tone={tone ?? 'neutral'}
            title={title}>
            {dot && <span className={styles.dot} aria-hidden="true" />}
            {icon != null && (
                <span className={styles.icon} aria-hidden="true">
                    {icon}
                </span>
            )}
            <span className={styles.label}>{children}</span>
            {iconEnd != null && (
                <span className={styles.icon} aria-hidden="true">
                    {iconEnd}
                </span>
            )}
        </span>
    );
}

export interface BadgeGroupProps {
    children: ReactNode;
    className?: string;
}

/** Badges side by side with the standard gap, such as a status and its "Draft" mark. */
export function BadgeGroup(props: BadgeGroupProps) {
    const { children, className } = props;

    return <div className={classNames(styles.group, className)}>{children}</div>;
}

export default Badge;
